import React, { useState, useEffect, useContext } from "react";
import { Container, Button, Input, Label, Row, Col, Alert } from "reactstrap";
import { useNavigate, useParams } from "react-router-dom";
import styles from "../css/ambulatoryVisit.module.scss";
import { useAmbulatoryVisitService } from "../api/useAmbulatoryVisitQuery";
import addVolunteerStyle from "../../../../modules/Volunteer/MasterVolunteer/css/volunteer.module.scss";
import SectionCard from "Components/Common/SectionCard";
import CommonTableIndexWise from "Components/Common/CommonTableIndexWise";
import formStyles from "../../../../assets/scss/common/formStyle.module.scss";
import FilePreviewModal from "Components/Common/FilePreviewModal";
import { formatDate, formatTime24Hour, showToastMessage } from "common/service/CommonService";
import { AMBULATORY_VISIT_PATHS } from "Routes/paths";
import FullPageLoader from "Components/Common/FullPageLoader";
import { BreadcrumbContext } from "Layouts/Header";
import TruncatedText from "Components/Common/TruncatedText";
import Commonbadge from "../../../../Components/Common/Commonbadge";
import { DROPOUT_REASONS_OPTIONS, DROPOUT_TYPE_OPTIONS, BLOCK_REASONS_OPTIONS, BLOOD_COLLECTION_REMARKS_OPTIONS, loadCodelistsDetailId } from "api/modules/common.option";
import DetailField from "../../../../Components/Common/DetailField";
import CRFScreeningModal from "modules/Volunteer/AssigningStudyModule/StudyScreening/components/CRFScreeningModal";

interface AmbulatoryFile extends File {
  attachmenttype?: string | null;
  visitactivityid?: number;
}



const cleanTimeFor24h = (val?: string | null) => {
  if (!val) return null;
  return String(val).split("+")[0].split("Z")[0].trim();
};

const ViewAmbulatorySubjectDetail: React.FC = () => {
  document.title = "View Ambulatory Subject Detail | ZENTIXS VISTA";

  const { visitId, subjectId } = useParams<{ visitId: string; subjectId: string }>();
  const studyid = Number(sessionStorage.getItem("activestudyid") || 1);
  const breadcrumbContext = useContext(BreadcrumbContext);
  const setBreadcrumbItems = breadcrumbContext?.setBreadcrumbItems;
  const navigate = useNavigate();

  useEffect(() => {
    setBreadcrumbItems?.([
      { text: "Ambulatory Visits", link: "/study/detail/ambulatory" },
      { text: "Visit Details", link: AMBULATORY_VISIT_PATHS.DETAIL.replace(":visitId", visitId || "") },
      { text: "View Subject Activity" },
    ]);
    return () => setBreadcrumbItems?.([]);
  }, [setBreadcrumbItems, visitId]);

  const [visit, setVisit] = useState<any>(null);
  const [subject, setSubject] = useState<any>(null);
  const [subjectAssignmentId, setSubjectAssignmentId] = useState<number | null>(null);

  const [previewFile, setPreviewFile] = useState<AmbulatoryFile | null>(null);
  const [previewFileUrl, setPreviewFileUrl] = useState<string | null>(null);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [comments, setComments] = useState("");
  const [activitiesExpanded, setActivitiesExpanded] = useState(false);
  const [selectedCRFPreview, setSelectedCRFPreview] = useState<any>(null);
  const [isCRFPreviewModalOpen, setIsCRFPreviewModalOpen] = useState<boolean>(false);

  const [decisionDetails, setDecisionDetails] = useState<any>({
    status: "",
    dropouttype: "",
    aereferenceno: "",
    dropoutdate: "",
    studystage: "",
    effectivedate: "",
    dropoutreason: "",
    detailedreason: "",
    blocktype: "Temporary",
    blockstartdate: "",
    blockenddate: "",
    blockduration: "0 days",
    blockreason: "",
    detailedremarks: "",
    rescreeningeligibility: "",
  });

  useEffect(() => {
    loadCodelistsDetailId([
      "DROPOUT_TYPE_OPTIONS",
      "DROPOUT_REASONS_OPTIONS",
      "BLOCK_REASONS_OPTIONS",
      "BLOOD_COLLECTION_REMARKS_OPTIONS",
    ]).then((result) => {
      const allLoaded = Object.values(result).every((flag) => flag);
      if (!allLoaded) {
        showToastMessage("error", "Some dropdown options failed to load");
      }
    });
  }, []);

  const handlePreview = (file: any) => {
      const API_BASE_URL =  sessionStorage.getItem("baseUrl") || process.env.REACT_APP_BASE_URL
      const baseUrl = API_BASE_URL?.replace("/api", "") || "";

    // NEW FILE (File object)
    if (file instanceof File) {
      setPreviewFile(file);
      setPreviewFileUrl(null);
      setPreviewOpen(true);
      return;
    }

    // EXISTING FILE (API object)
    const path = file?.filepath || file?.path;

    if (!path) {
      console.error("Invalid file object:", file);
      return;
    }

    const finalUrl = path.startsWith("http")
      ? path
      : `${baseUrl}${path.startsWith("/") ? path : "/" + path}`;

    setPreviewFile(null);
    setPreviewFileUrl(finalUrl);
    setPreviewOpen(true);
  };

  const {
    SelectdataAmbulatoryVisit,
    SelectAllAssignedSubjects,
    SelectdataAmbulatoryVisitActivityResult,
    SelectAmbulatoryVisitSubjectAssessment,
    SelectAmbulatoryVisitSubjectBloodCollection,
  } = useAmbulatoryVisitService({
    studyid,
    visitid: Number(visitId),
    subjectassignmentid: subjectAssignmentId || undefined,
  });

  const assessmentData = (() => {
    if (!SelectAmbulatoryVisitSubjectAssessment?.data) return null;
    const res = (SelectAmbulatoryVisitSubjectAssessment.data as any)?.data;
    return Array.isArray(res) ? res[0] : res;
  })();

  const bloodCollectionData = (() => {
    if (!SelectAmbulatoryVisitSubjectBloodCollection?.data) return null;
    const res = (SelectAmbulatoryVisitSubjectBloodCollection.data as any)?.data;
    return Array.isArray(res) ? res[0] : res;
  })();

  const isSection1Completed = Boolean(
    assessmentData &&
      (assessmentData.subjectassessmentid ||
        assessmentData.subjectreported !== undefined ||
        assessmentData.subjectassessmentstatus === "completed")
  );

  const isSection2Completed = Boolean(
    bloodCollectionData &&
      (bloodCollectionData.subjectbloodcollectionid ||
        bloodCollectionData.bloodcollectionstatus === "completed" ||
        bloodCollectionData.wellbeing !== undefined)
  );

  useEffect(() => {
    if (SelectAllAssignedSubjects.data) {
      const subjectsList = (SelectAllAssignedSubjects.data as any)?.data || [];
      const foundSub = subjectsList.find((s: any) => String(s.subjectno || s.subjectId) === String(subjectId));
      if (foundSub) {
        const assId = Number(foundSub.visitsubjectassignmentid || foundSub.id || 0);
        if (assId && assId !== subjectAssignmentId) {
          setSubjectAssignmentId(assId);
        }
      }
    }
  }, [SelectAllAssignedSubjects.data, subjectId, subjectAssignmentId]);

  useEffect(() => {
    if (SelectdataAmbulatoryVisit.data && SelectAllAssignedSubjects.data) {
      const visitRes = (SelectdataAmbulatoryVisit.data as any)?.data;
      const visitData = visitRes?.visit?.[0];
      setVisit({
        id: visitData?.visitid || visitId,
        visitNumber: visitData?.visitnumber || `Visit ${String(visitData?.visitid || visitId).padStart(2, "0")}`,
        visitName: visitData?.visitname || "--",
        scheduledDate: visitData?.scheduleddate ? new Date(visitData.scheduleddate).toLocaleDateString([], { day: '2-digit', month: 'short', year: 'numeric' }) : "30 Jun 2026",
        visitStatus: visitData?.visitstatus || "scheduled",
      });

      const subjectsList = (SelectAllAssignedSubjects.data as any)?.data || [];
      const foundSub = subjectsList.find((s: any) => String(s.subjectno || s.subjectId) === String(subjectId));
      if (foundSub) {
        if (SelectdataAmbulatoryVisitActivityResult.data) {
          const apiRes = (SelectdataAmbulatoryVisitActivityResult.data as any)?.data;
          const results = apiRes?.activityresults || [];
          const attachmentsList = apiRes?.attachments || [];
          const decStatus = apiRes?.decisionstatus || foundSub.subjectvisitstatus || foundSub.status || "";
          const decRemarks = apiRes?.decisionremarks || "";
          const decInfo = apiRes?.decisioninfo || {};

          const mappedActivities = (visitRes?.activities || []).map((act: any) => {
            const actId = act.activityid || act.id;
            const savedResult = results.find((r: any) => Number(r.visitactivityid) === Number(actId));
            const isMandatory =
              act.ismandatory === true || act.ismandatory === 1 || String(act.ismandatory).toLowerCase() === "true" ||
              act.mandatory === true || act.mandatory === 1 || String(act.mandatory).toLowerCase() === "true";

            return {
              id: actId,
              name: act.activityname || act.name,
              status: savedResult?.subjectactivityresult ? "Completed" : "Not Started",
              result: savedResult?.subjectactivityresult || null,
              performedDate: savedResult?.createddate ? new Date(savedResult.createddate).toISOString().split('T')[0] : "",
              performedTime: "",
              performedBy: savedResult?.createdby || "",
              remarks: savedResult?.remarks || "",
              ismandatory: isMandatory
            };
          });

          setSubject({
            ...foundSub,
            subjectId: foundSub.subjectno || subjectId,
            initials: foundSub.initials || foundSub.subjectname?.slice(0, 2).toUpperCase() || "SJ",
            checkinTime: foundSub.checkintime || foundSub.checkinTime || "09:00 AM",
            status: decStatus.toLowerCase(),
            activities: mappedActivities
          });

          setComments(decRemarks);

          const statusVal = decStatus.toLowerCase();
          const initStatus = ["completed", "dropped", "blocked"].includes(statusVal) ? statusVal : "";
          setDecisionDetails({
            status: initStatus,
            dropouttype: decInfo.dropouttype || "",
            aereferenceno: decInfo.aereferenceno || "",
            dropoutdate: decInfo.dropoutdate ? decInfo.dropoutdate.split("T")[0] : "",
            studystage: decInfo.studystage || "",
            effectivedate: decInfo.effectivedate ? decInfo.effectivedate.split("T")[0] : "",
            dropoutreason: decInfo.dropoutreason || "",
            detailedreason: decInfo.dropout_remarks || decInfo.detailedreason || "",
            blocktype: decInfo.blocktype || "Temporary",
            blockstartdate: decInfo.block_startdate ? decInfo.block_startdate.split("T")[0] : "",
            blockenddate: decInfo.block_enddate ? decInfo.block_enddate.split("T")[0] : "",
            blockduration: decInfo.blockduration || "0 days",
            blockreason: decInfo.blockreason || "",
            detailedremarks: decInfo.block_remarks || decInfo.detailedremarks || "",
            rescreeningeligibility: decInfo.rescreeningeligibility || "",
          });
        }
      }
    }
  }, [
    SelectdataAmbulatoryVisit.data,
    SelectAllAssignedSubjects.data,
    SelectdataAmbulatoryVisitActivityResult.data,
    subjectId,
    visitId
  ]);

  if (
    SelectdataAmbulatoryVisit.isLoading ||
    SelectdataAmbulatoryVisit.isFetching ||
    SelectAllAssignedSubjects.isLoading ||
    SelectAllAssignedSubjects.isFetching ||
    !subjectAssignmentId ||
    SelectdataAmbulatoryVisitActivityResult.isLoading ||
    SelectdataAmbulatoryVisitActivityResult.isFetching ||
    SelectAmbulatoryVisitSubjectAssessment.isLoading ||
    SelectAmbulatoryVisitSubjectAssessment.isFetching ||
    SelectAmbulatoryVisitSubjectBloodCollection.isLoading ||
    SelectAmbulatoryVisitSubjectBloodCollection.isFetching ||
    !subject ||
    !visit
  ) {
    return <FullPageLoader message="Loading Subject Details..." />;
  }

  const apiAttachments = (SelectdataAmbulatoryVisitActivityResult.data as any)?.data?.attachments || [];

  const activityColumns = [
    {
      key: "name",
      label: "Activity Name",
      render: (val: any, row: any) => (
        <span className="fw-bold text-dark me-2 fs-13 d-inline-flex align-items-center">
          <TruncatedText text={val || "--"} maxWidth="200px" />
          {row.ismandatory === true && <span className="text-danger ms-1">*</span>}
        </span>
      ),
    },
    {
      key: "result",
      label: "Result",
      render: (_: any, row: any) => (
        <div className="d-flex align-items-center gap-3 fs-13">
          <Label className="d-flex align-items-center gap-1 mb-0 small fw-semibold text-success fs-13">
            <Input
              type="checkbox"
              checked={row.result === "Passed"}
              disabled={true}
              style={{ borderRadius: "4px", height: "17px" }}
            />
            Passed
          </Label>
          <Label className="d-flex align-items-center gap-1 mb-0 small fw-semibold text-danger fs-13">
            <Input
              type="checkbox"
              checked={row.result === "Failed"}
              disabled={true}
              style={{ borderRadius: "4px", height: "17px" }}
            />
            Failed
          </Label>
        </div>
      ),
    },
    {
      key: "remarks",
      label: "Remarks",
      render: (val: any) => (
        <TruncatedText text={val || "--"} maxWidth="850px" />
      ),
    }
  ];

  return (
    <div className={`page-content ${formStyles.pageContainer}`}>
      <Container fluid>

        {/* Subject Header Card */}
        <SectionCard
          icon="ri-user-search-line"
          title="Subject Identification"
          subtitle="Details of the subject and active visit check-in status."
        >
          <div className="d-flex align-items-center justify-content-between flex-wrap gap-3 w-100">
            <div className="d-flex align-items-center gap-3 w-100">
              <div className={`${styles.detailMetaGrid} flex-grow-1`}>
                <div className={`${styles.metaBlock} d-flex align-items-center gap-2`}>
                  <i className="ri-user-3-line fs-20 text-muted" style={{ marginRight: "4px" }} />
                  <div>
                    <div className={styles.metaLabel}>Subject No.</div>
                    <div className="d-flex align-items-center gap-2">
                      <span className="fw-bold text-dark" style={{ fontSize: "14px" }}>{subject.subjectId}</span>
                    </div>
                  </div>
                </div>

                <div className={`${styles.metaBlock} d-flex align-items-center gap-2`}>
                  <i className="ri-passport-line fs-20 text-muted" style={{ marginRight: "4px" }} />
                  <div>
                    <div className={styles.metaLabel}>Visit Number</div>
                    <div className="d-flex align-items-center gap-2">
                      <span className="fw-bold text-dark" style={{ fontSize: "14px" }}>{visit.visitNumber}</span>
                    </div>
                  </div>
                </div>

                <div className={`${styles.metaBlock} d-flex align-items-center gap-2`}>
                  <i className="ri-bookmark-3-line fs-20 text-muted" style={{ marginRight: "4px" }} />
                  <div>
                    <div className={styles.metaLabel}>Visit Name</div>
                    <div className={styles.metaValue} style={{ color: "#0f172a", fontSize: "14px", fontWeight: 700 }}>
                      <TruncatedText text={visit.visitName || "--"} maxWidth="150px" />
                    </div>
                  </div>
                </div>

                <div className={`${styles.metaBlock} d-flex align-items-center gap-2`}>
                  <i className="ri-calendar-line fs-20 text-muted" style={{ marginRight: "4px" }} />
                  <div>
                    <div className={styles.metaLabel}>Visit Date</div>
                    <div className={styles.metaValue} style={{ color: "#0f172a", fontSize: "14px", fontWeight: 700 }}>{formatDate(visit.scheduledDate)}</div>
                  </div>
                </div>

                <div className={`${styles.metaBlock} d-flex align-items-center gap-2`}>
                  <i className="ri-time-line fs-20 text-muted" style={{ marginRight: "4px" }} />
                  <div>
                    <div className={styles.metaLabel}>Check-In</div>
                    <div className={styles.metaValue} style={{ color: "#0f172a", fontSize: "14px", fontWeight: 700 }}>
                      {subject.checkintime ? subject.checkintime.split(" ")[0] : "09:00 AM"}
                    </div>
                  </div>
                </div>

                <div className={`${styles.metaBlock} d-flex align-items-center gap-2`}>
                  <i className="ri-checkbox-circle-fill fs-20 text-muted" style={{ marginRight: "4px" }} />
                  <div>
                    <div className={styles.metaLabel}>Subject Visit Status</div>
                    <Commonbadge status={subject?.subjectvisitstatus || "checked in"} style={{ width: "140px", fontSize: "12px" }} />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </SectionCard>

        {/* Column Layout / Content Panels */}
        <div className="d-flex flex-column">

          {/* Configured CRFs Section */}
          {(() => {
            const visitResData = (SelectdataAmbulatoryVisit.data as any)?.data;
            const allVisitCrfs: any[] = visitResData?.visitcrf || [];
            const subjectCrfList = allVisitCrfs.filter((item: any) => {
              if (!item.subjectno) return true;
              return String(item.subjectno).trim() === String(subjectId || subject?.subjectId || "").trim();
            });

            if (!subjectCrfList || subjectCrfList.length === 0) return null;

            return (
              <SectionCard
                icon="ri-article-line"
                title="Configured CRFs"
                subtitle="View form details and answers for configured visit CRFs."
              >
                <div className="p-3 border rounded-3 bg-white">
                  <div className="text-muted fs-13 d-flex align-items-center gap-1 mb-2">
                    <i className="ri-information-line fs-15" />
                    <span>Click on CRF to view form & answers</span>
                  </div>
                  <div className="d-flex flex-wrap gap-2">
                    {subjectCrfList.map((crfItem: any, idx: number) => {
                      const title = crfItem.crfquestiondetails?.title || crfItem.crfname || crfItem.name || `CRF #${crfItem.subjectassignmentresultid || idx + 1}`;
                      const answers = crfItem.crfanswerdetails;
                      const st = (crfItem.crfstatus || "").toLowerCase().trim();
                      const isFilled = st === "completed" || (answers && typeof answers === "object" && Object.keys(answers).length > 0);
                      return (
                        <button
                          key={crfItem.subjectassignmentresultid || crfItem.visitcrfid || idx}
                          type="button"
                          onClick={() => {
                            const schema = crfItem.crfquestiondetails || crfItem.crfdetails || crfItem.json || {};
                            const crfFormObj = {
                              id: crfItem.subjectassignmentresultid || crfItem.visitcrfid || idx,
                              subjectassignmentresultid: crfItem.subjectassignmentresultid,
                              name: title,
                              crfid: crfItem.crfid || crfItem.id,
                              crfdetails: {
                                ...schema,
                                answers: answers || null,
                              },
                              crfanswerdetails: answers || null,
                              selectedCrfDetails: {
                                crfdetails: {
                                  ...schema,
                                  answers: answers || null,
                                },
                                title: title,
                                answers: answers || null,
                              },
                            };
                            setSelectedCRFPreview(crfFormObj);
                            setIsCRFPreviewModalOpen(true);
                          }}
                          className="btn btn-sm rounded-pill px-3 py-2 fs-13 border-0 shadow-none d-inline-flex align-items-center cursor-pointer"
                          style={{
                            backgroundColor: isFilled ? "#eef9f2" : "#e0f2fe",
                            color: "#1f2937",
                            transition: "all 0.15s ease-in-out",
                          }}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.backgroundColor = isFilled ? "#e2f4e8" : "#d3e7f5";
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.backgroundColor = isFilled ? "#eef9f2" : "#e0f2fe";
                          }}
                        >
                          <i className={`ri-file-text-line fs-16 me-2 ${isFilled ? "text-success" : "text-primary"}`} />
                          <span className="fw-bold text-dark me-2">{title}</span>
                          <span className={`d-inline-flex align-items-center gap-1 fs-12 fw-semibold ${isFilled ? "text-success" : "text-primary"}`}>
                            <span
                              style={{
                                width: "6px",
                                height: "6px",
                                borderRadius: "50%",
                                backgroundColor: isFilled ? "#10b981" : "#1e6b91",
                                display: "inline-block",
                              }}
                            />
                            <span>{isFilled ? "Completed" : "Pending"}</span>
                          </span>
                        </button>
                      );
                    })}
                  </div>
                </div>
              </SectionCard>
            );
          })()}

          {/* Subject Screening Section */}
          <SectionCard
            icon="ri-user-search-line"
            title="Subject Screening"
            subtitle="Subject screening and breath alcohol test details recorded by clinical staff."
            rightContent={
              isSection1Completed ? (
                <span className="badge bg-success-subtle text-success fs-12 px-2.5 py-1.5 rounded-pill border border-success d-inline-flex align-items-center gap-1">
                  <i className="ri-checkbox-circle-line" /> Completed
                </span>
              ) : (
                <span className="badge bg-warning-subtle text-warning fs-12 px-2.5 py-1.5 rounded-pill border border-warning d-inline-flex align-items-center gap-1">
                  <i className="ri-time-line" /> Pending Completion
                </span>
              )
            }
          >
            <Row>
              <DetailField
                label="Participant Reported"
                value={
                  assessmentData?.subjectreported === true || String(assessmentData?.subjectreported).toLowerCase() === "yes"
                    ? "Yes"
                    : assessmentData?.subjectreported === false || String(assessmentData?.subjectreported).toLowerCase() === "no"
                    ? "No"
                    : assessmentData?.subjectreported != null
                    ? String(assessmentData.subjectreported)
                    : null
                }
                columns={4}
              />
              <DetailField
                label="ID Card Given"
                value={
                  assessmentData?.idcardgiven === true || String(assessmentData?.idcardgiven).toLowerCase() === "yes"
                    ? "Yes"
                    : assessmentData?.idcardgiven === false || String(assessmentData?.idcardgiven).toLowerCase() === "no"
                    ? "No"
                    : assessmentData?.idcardgiven != null
                    ? String(assessmentData.idcardgiven)
                    : null
                }
                columns={4}
              />
              <DetailField
                label="Alcohol Test Result"
                value={
                  assessmentData?.breathalcoholresult
                    ? String(assessmentData.breathalcoholresult).charAt(0).toUpperCase() + String(assessmentData.breathalcoholresult).slice(1)
                    : null
                }
                columns={4}
              />
              <DetailField
                label="Color of indicator light"
                value={assessmentData?.breathalcoholcolor || null}
                columns={4}
              />
              <DetailField
                label="Alcohol concentration (mg/100ml)"
                value={
                  assessmentData?.breathalcoholconcentration !== undefined && assessmentData?.breathalcoholconcentration !== null
                    ? String(assessmentData.breathalcoholconcentration)
                    : null
                }
                columns={4}
              />
              <DetailField
                label="Identification Time (By ID card)"
                value={formatTime24Hour(cleanTimeFor24h(assessmentData?.identificationtime), false)}
                columns={4}
              />
            </Row>
          </SectionCard>

          {/* Sample Collection Section */}
          <SectionCard
            icon="ri-test-tube-line"
            title="Sample Collection"
            subtitle="Blood sample collection timing, ice bath status, and deviation details recorded by phlebotomist."
            rightContent={
              isSection2Completed ? (
                <span className="badge bg-success-subtle text-success fs-12 px-2.5 py-1.5 rounded-pill border border-success d-inline-flex align-items-center gap-1">
                  <i className="ri-checkbox-circle-line" /> Completed
                </span>
              ) : (
                <span className="badge bg-warning-subtle text-warning fs-12 px-2.5 py-1.5 rounded-pill border border-warning d-inline-flex align-items-center gap-1">
                  <i className="ri-time-line" /> Pending Completion
                </span>
              )
            }
          >
            <Row>
              <DetailField
                label="Well Being"
                value={
                  bloodCollectionData?.wellbeing === true || String(bloodCollectionData?.wellbeing).toLowerCase() === "well"
                    ? "Well"
                    : bloodCollectionData?.wellbeing === false || String(bloodCollectionData?.wellbeing).toLowerCase() === "unwell"
                    ? "Unwell"
                    : bloodCollectionData?.wellbeing != null
                    ? String(bloodCollectionData.wellbeing)
                    : null
                }
                columns={4}
              />
              <DetailField
                label="Light Condition"
                value={
                  bloodCollectionData?.lightcondition === true || String(bloodCollectionData?.lightcondition).toLowerCase() === "normal"
                    ? "Normal"
                    : bloodCollectionData?.lightcondition === false || String(bloodCollectionData?.lightcondition).toLowerCase() === "sodium"
                    ? "Sodium Vapor"
                    : bloodCollectionData?.lightcondition != null
                    ? String(bloodCollectionData.lightcondition)
                    : null
                }
                columns={4}
              />
              <DetailField
                label="ID Card Returned?"
                value={
                  bloodCollectionData?.idcardreturned === true || String(bloodCollectionData?.idcardreturned).toLowerCase() === "yes"
                    ? "Yes"
                    : bloodCollectionData?.idcardreturned === false || String(bloodCollectionData?.idcardreturned).toLowerCase() === "no"
                    ? "No"
                    : bloodCollectionData?.idcardreturned != null
                    ? String(bloodCollectionData.idcardreturned)
                    : null
                }
                columns={4}
              />
              <DetailField
                label="Blood Sample Scheduled Time"
                value={formatTime24Hour(
                  cleanTimeFor24h(
                    bloodCollectionData?.bloodsamplescheduledtime ||
                      bloodCollectionData?.scheduledtime ||
                      ((SelectdataAmbulatoryVisit?.data as any)?.data?.samplingpoints?.[0]?.scheduledtime ||
                       (SelectdataAmbulatoryVisit?.data as any)?.data?.samplingpoints?.[0]?.time)
                  ),
                  false
                )}
                columns={4}
              />
              <DetailField
                label="Blood Sample Actual Time"
                value={formatTime24Hour(cleanTimeFor24h(bloodCollectionData?.bloodsampleactualtime), false)}
                columns={4}
              />
              <DetailField
                label="Kept in Wet Ice Bath?"
                value={
                  bloodCollectionData?.keptinicebath === true || String(bloodCollectionData?.keptinicebath).toLowerCase() === "yes"
                    ? "Yes"
                    : bloodCollectionData?.keptinicebath === false || String(bloodCollectionData?.keptinicebath).toLowerCase() === "no"
                    ? "No"
                    : bloodCollectionData?.keptinicebath != null
                    ? String(bloodCollectionData.keptinicebath)
                    : null
                }
                columns={4}
              />
            </Row>
            <Row className="mt-2">
              <DetailField
                label="Remarks"
                value={
                  (() => {
                    if (bloodCollectionData?.remarks == null || bloodCollectionData?.remarks === "") return null;
                    const opt = BLOOD_COLLECTION_REMARKS_OPTIONS.find(
                      (o) => String(o.value) === String(bloodCollectionData.remarks) || o.label === bloodCollectionData.remarks
                    );
                    return opt ? opt.label : bloodCollectionData.remarksname || String(bloodCollectionData.remarks);
                  })()
                }
                columns={6}
              />
              <DetailField
                label="Additional Remarks"
                value={bloodCollectionData?.description || null}
                columns={6}
                expandable={true}
                maxChars={200}
              />
            </Row>
          </SectionCard>

          {/* Card 3: Overall Decision */}
          <SectionCard
            icon="ri-shield-check-line"
            title="Overall Decision"
            subtitle="Record the overall decision or action applicable for this subject for this visit."
          >
            <div className="mb-3 pb-3 border-bottom">
              <Row>
                <Col md={12}>
                  <label className="text-muted fs-12 mb-1 text-uppercase fw-600 ls-1 d-block">
                    Overall Decision Status
                  </label>
                  <div style={{ minHeight: "24px" }}>
                    <Commonbadge
                      status={subject.status || "completed"}
                      style={{ width: "130px", fontSize: "13px", padding: "6px 12px" }}
                    />
                  </div>
                </Col>
              </Row>
            </div>

            {/* Dropped (Discontinued) Details */}
            {(subject.status?.toLowerCase() === "dropped") && (() => {
              const selectedType = DROPOUT_TYPE_OPTIONS.find(
                (opt) => String(opt.value) === String(decisionDetails.dropouttype) || opt.label === decisionDetails.dropouttype
              )?.label || (decisionDetails.dropouttype ? String(decisionDetails.dropouttype) : "—");

              const selectedReason = DROPOUT_REASONS_OPTIONS.find(
                (opt) => String(opt.value) === String(decisionDetails.dropoutreason) || opt.label === decisionDetails.dropoutreason
              )?.label || (decisionDetails.dropoutreason ? String(decisionDetails.dropoutreason) : "—");

              const isAE = selectedType === "Medical / AE";

              return (
                <div className="p-3 border rounded bg-light-subtle mb-4 shadow-sm">
                  <h6 className="fw-bold text-dark mb-3 border-bottom pb-2 font-size-13">
                    <i className="ri-logout-box-r-line me-2 text-warning" />
                    Dropout Details
                  </h6>

                  {isAE && (
                    <div className="mt-2 mb-3">
                      <Alert color="danger" className="border-0 bg-light-danger text-danger d-flex flex-column gap-1 p-3">
                        <div className="fw-semibold">
                          <i className="ri-error-warning-line me-2" />
                          Medical / Adverse Event Dropout Selected
                        </div>
                      </Alert>
                    </div>
                  )}

                  <Row>
                    <DetailField label="Dropout Type" value={selectedType} columns={4} />
                    {isAE && <DetailField label="AE Reference No." value={decisionDetails.aereferenceno} columns={4} />}
                    <DetailField label="Dropout Date" value={decisionDetails.dropoutdate ? formatDate(decisionDetails.dropoutdate) : null} columns={4} />
                    <DetailField label="Study Stage" value={decisionDetails.studystage} columns={4} />
                    <DetailField label="Effective Date" value={decisionDetails.effectivedate ? formatDate(decisionDetails.effectivedate) : null} columns={4} />
                    <DetailField label="Dropout Reason" value={selectedReason} columns={4} />
                  </Row>
                  <Row className="mt-2">
                    <DetailField
                      label="Detailed Reason"
                      value={decisionDetails.detailedreason || "No detailed reason provided."}
                      columns={12}
                      expandable={true}
                      maxChars={200}
                    />
                  </Row>
                </div>
              );
            })()}

            {/* Blocked Details */}
            {(subject.status?.toLowerCase() === "blocked") && (() => {
              const isPermanent = decisionDetails.blocktype === "Permanent";

              const selectedReason = BLOCK_REASONS_OPTIONS.find(
                (opt) => String(opt.value) === String(decisionDetails.blockreason) || opt.label === decisionDetails.blockreason
              )?.label || (decisionDetails.blockreason ? String(decisionDetails.blockreason) : "—");

              return (
                <div className="p-3 border rounded bg-light-subtle mb-4 shadow-sm">
                  <h6 className="fw-bold text-dark mb-3 border-bottom pb-2 font-size-13">
                    <i className="ri-forbid-2-line me-2 text-danger" />
                    Block Subject Details
                  </h6>
                  <Row>
                    <DetailField label="Block Type" value={decisionDetails.blocktype} columns={4} />
                    <DetailField label="Block Start Date" value={decisionDetails.blockstartdate ? formatDate(decisionDetails.blockstartdate) : null} columns={4} />
                    {!isPermanent && (
                      <>
                        <DetailField label="Block End Date" value={decisionDetails.blockenddate ? formatDate(decisionDetails.blockenddate) : null} columns={4} />
                        <DetailField label="Block Duration" value={decisionDetails.blockduration} columns={4} />
                      </>
                    )}
                    <DetailField label="Block Reason" value={selectedReason} columns={4} />
                    {!isPermanent && (
                      <DetailField label="Re-screening Eligibility" value={decisionDetails.rescreeningeligibility} columns={4} />
                    )}
                  </Row>
                  <Row className="mt-2">
                    <DetailField
                      label="Detailed Remarks"
                      value={decisionDetails.detailedremarks || "No detailed remarks provided."}
                      columns={12}
                      expandable={true}
                      maxChars={200}
                    />
                  </Row>
                </div>
              );
            })()}

            {/* Comments */}
            <Row>
              <DetailField
                label="Additional Comments / Discrepancies"
                value={comments || "No comments provided."}
                columns={12}
                expandable={true}
                maxChars={200}
              />
            </Row>
          </SectionCard>

        </div>
      </Container>

      {/* Sticky Footer */}
      <div className={formStyles.stickyFooter}>
        <div className="px-3">
          <div className={formStyles.footerActions}>
            <Button
              type="button"
              color="dark"
              outline
              onClick={() => navigate(AMBULATORY_VISIT_PATHS.DETAIL.replace(":visitId", visitId || ""))}
            >
              Back
            </Button>
          </div>
        </div>
      </div>

      {/* File Preview Modal */}
      <FilePreviewModal
        file={previewFile}
        fileUrl={previewFileUrl}
        isOpen={previewOpen}
        onClose={() => setPreviewOpen(false)}
      />

      {/* CRF Screening / View Modal */}
      {selectedCRFPreview && (
        <CRFScreeningModal
          isOpen={isCRFPreviewModalOpen}
          toggle={() => {
            setIsCRFPreviewModalOpen(false);
            setSelectedCRFPreview(null);
          }}
          selectedCrf={selectedCRFPreview}
          readonly={true}
          onSave={() => {}}
          isPending={false}
          onClose={() => {
            setIsCRFPreviewModalOpen(false);
            setSelectedCRFPreview(null);
          }}
          parentBreadcrumbs={[
            { text: "Ambulatory Visits", link: "/study/detail/ambulatory" },
            { text: "Visit Details", link: AMBULATORY_VISIT_PATHS.DETAIL.replace(":visitId", visitId || "") },
            { text: "View Subject Activity" ,  onclick:()=>{
 setIsCRFPreviewModalOpen(false);
            setSelectedCRFPreview(null);
            }},
          ]}
        />
      )}
    </div>
  );
};

export default ViewAmbulatorySubjectDetail;
